import { useState } from 'react';
import { Modal } from '@components/ui/Modal';
import { Textarea } from '@components/ui';
import Button from '@components/ui/Button/Button';
import { useTranslation } from '@hooks/useTranslation';
import styles from './DeactivateModal.module.scss';

interface DeactivateModalProps {
  isOpen: boolean;
  snapshotName: string;
  onClose: () => void;
  onConfirm: (reason: string) => void;
  loading?: boolean;
}

export function DeactivateModal({ isOpen, snapshotName, onClose, onConfirm, loading }: DeactivateModalProps) {
  const { t } = useTranslation();
  const [reason, setReason] = useState('');
  const [error, setError] = useState('');

  const handleClose = () => {
    if (loading) return;
    setReason('');
    setError('');
    onClose();
  };

  const handleConfirm = () => {
    const trimmed = reason.trim();
    if (!trimmed) {
      setError(t('K_VALIDATION_REASON_REQUIRED', 'Reason is required'));
      return;
    }
    if (trimmed.length > 500) {
      setError(t('K_VALIDATION_REASON_MAX_500', 'Reason must be 500 characters or less'));
      return;
    }
    setError('');
    onConfirm(trimmed);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={t('K_SNAPSHOT_ADMIN_DEACTIVATE_TITLE', 'Deactivate Contract')}
      size="sm"
      footer={
        <div className={styles.footer}>
          <Button variant="outline" size="sm" onClick={handleClose} disabled={loading}>
            {t('K_CANCEL', 'Cancel')}
          </Button>
          <Button variant="danger" size="sm" onClick={handleConfirm} loading={loading}>
            {t('K_DS_DEACTIVATE', 'Deactivate')}
          </Button>
        </div>
      }
    >
      <div className={styles.content}>
        <p className={styles.description}>
          {t('K_SNAPSHOT_ADMIN_DEACTIVATE_DESC', 'Are you sure you want to deactivate "{name}"? The client will no longer be able to use this contract.').replace('{name}', snapshotName)}
        </p>
        <Textarea
          label={t('K_LABEL_REASON', 'Reason')}
          isRequired
          placeholder={t('K_PLACEHOLDER_DEACTIVATE_REASON', 'Explain why this contract is being deactivated...')}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          error={error}
          rows={4}
          maxLength={500}
        />
        <span className={styles.charCount}>{reason.length} / 500</span>
      </div>
    </Modal>
  );
}
